import { TableClient } from "@azure/data-tables";
import type { TokenCredential } from "@azure/identity";
import {
  TABLE, UPDATE_POLICY_PK, UPDATE_POLICY_RK,
  type UpdatePolicy, type PolicyMode,
  isPolicyMode, updatePolicyToEntity, entityToUpdatePolicy,
} from "@m8t-stack/platform-release";
import { upsertMetadataEntity, makeSharedKeyClient, is403, is404, type TableLike } from "./metadata-table-write.js";
import { discoverStampStorage } from "./platform-storage-discovery.js";
import { LocalCliError } from "./errors.js";

/** Build a policy record. Pure. Throws on an unrecognized mode. */
export function buildPolicy(mode: PolicyMode, nowIso: string, updatedBy: string): UpdatePolicy {
  if (!isPolicyMode(mode)) {
    throw new LocalCliError({
      code: "PLATFORM_POLICY_MODE_INVALID",
      message: `Unrecognized update mode "${String(mode)}".`,
      hint: "Use one of: notify-only, auto-critical, auto-all.",
    });
  }
  return { schemaVersion: 1, mode, updatedAt: nowIso, updatedBy };
}

export async function writePolicy(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  policy: UpdatePolicy;
  onProgress?: (m: string) => void;
}): Promise<void> {
  const entity = updatePolicyToEntity(opts.policy) as Record<string, unknown> & {
    partitionKey: string; rowKey: string;
  };
  await upsertMetadataEntity({
    credential: opts.credential,
    subscriptionId: opts.subscriptionId,
    resourceGroup: opts.resourceGroup,
    entity,
    label: "update policy",
    errorCode: "PLATFORM_POLICY_NO_ACCESS",
    retryHint: "The role assignment persists — retry in a few minutes.",
    ...(opts.onProgress ? { onProgress: opts.onProgress } : {}),
  });
}

/**
 * The three distinct outcomes a policy read can have. Unlike a plain
 * `UpdatePolicy | null`, "the record genuinely does not exist" and "the read
 * failed and the live setting is unknown" cannot collapse into the same
 * value — a caller must handle `unreadable` explicitly rather than treating
 * it as "absent" (which would present the built-in default as the truth for
 * a setting that controls whether updates apply unattended).
 */
export type PolicyReadResult =
  | { source: "absent" }
  | { source: "explicit"; policy: UpdatePolicy }
  | { source: "unreadable" };

/**
 * Read the policy row. Mirrors `readStamp`'s auth ladder (see
 * platform-stamp.ts): a 403 on the AAD read falls back to a shared-key read
 * before giving up, because a permission error is the expected condition for
 * an identity that holds broad control-plane rights but lacks the storage
 * data role — the exact identity the write-side ladder exists to serve.
 *
 * A 404 is the only case that reports `absent`. Every other failure —
 * including the shared-key fallback being unavailable or also failing, and a
 * row whose stored value fails to parse — reports `unreadable` rather than
 * guessing. This function never returns the built-in default; callers decide
 * what to show for each outcome.
 */
export async function readPolicy(opts: {
  credential: TokenCredential;
  subscriptionId: string;
  resourceGroup: string;
  // Test seams — production callers pass none of these.
  discoverImpl?: typeof discoverStampStorage;
  aadClientImpl?: (tableEndpoint: string, credential: TokenCredential) => TableLike;
  sharedClientImpl?: typeof makeSharedKeyClient;
}): Promise<PolicyReadResult> {
  const discover = opts.discoverImpl ?? discoverStampStorage;
  const makeAad =
    opts.aadClientImpl ?? ((endpoint: string, credential: TokenCredential): TableLike => new TableClient(endpoint, TABLE, credential));
  const makeShared = opts.sharedClientImpl ?? makeSharedKeyClient;

  const { tableEndpoint, accountResourceId, accountName } = await discover(opts);
  const aad = makeAad(tableEndpoint, opts.credential);
  try {
    const row = await aad.getEntity(UPDATE_POLICY_PK, UPDATE_POLICY_RK);
    return { source: "explicit", policy: entityToUpdatePolicy(row as { value: string }) };
  } catch (e) {
    if (is404(e)) return { source: "absent" };
    if (!is403(e)) return { source: "unreadable" };
  }

  try {
    const shared = await makeShared({ tableEndpoint, accountResourceId, accountName });
    if (!shared) return { source: "unreadable" };
    const row = await shared.getEntity(UPDATE_POLICY_PK, UPDATE_POLICY_RK);
    return { source: "explicit", policy: entityToUpdatePolicy(row as { value: string }) };
  } catch {
    return { source: "unreadable" };
  }
}
